import { useId, useState } from 'react';
import type { InputHTMLAttributes, Ref } from 'react';
import { compareDecimal, readDecimal, toLatinDigits } from '@workbook/shared';
import { Input } from './Input';

/**
 * 🔴 A NUMBER BOX THAT SAYS WHEN IT HOLDS NO NUMBER — design-system plan DS-5 (5.1; F511, F424, F497, F215).
 *
 * The value is the TEXT, as typed (Bengali digits already mapped to Latin by `Input`): a figure is never parsed into a
 * JavaScript number on the way through, which is how `12345678901234.5678` survives and how a half-typed `12.` is not
 * rewritten under the cursor. When the person LEAVES the box, the text is read with `readDecimal` against `decimals`,
 * `min` and `max` (compared digit by digit, `compareDecimal`); if it is not such a number, the box says which rule it
 * broke under itself (`role="alert"`) and is `aria-invalid`, instead of sending NaN or dropping the line. While the
 * person types nothing new is announced — a half-typed `5.` or `-` is not an error yet — but an error already shown
 * goes the moment the text is right. `onValue` hands the screen the canonical string (`"105000.5"`) or null.
 *
 * `kind`: `amount` (money, the figure face), `quantity`, `integer` (no decimals), `percent`. `unit` is shown after the
 * box and read with it (`aria-describedby`) — "kg", "%", "days". A negative is accepted only when `min` is below zero.
 */
export type NumberKind = 'amount' | 'quantity' | 'integer' | 'percent';

interface Rules {
  kind: NumberKind;
  decimals: number;
  min?: string;
  max?: string;
}

const places = ({ kind, decimals }: Rules) => (kind === 'integer' ? 0 : decimals);
const negativeAllowed = ({ min }: Rules) => min !== undefined && compareDecimal(min, '0') < 0;

/** The canonical figure, or null when the text is empty or breaks a rule. */
export function numberValue(text: string, rules: Rules): string | null {
  if (text.trim() === '' || problemWith(text, rules)) return null;
  return readDecimal(text, { decimals: places(rules), negative: negativeAllowed(rules) });
}

/** Which rule the text breaks, in words, or null. Empty is not a problem here: `required` is the form's. */
export function problemWith(text: string, rules: Rules): string | null {
  if (text.trim() === '') return null;
  const value = readDecimal(text, { decimals: places(rules), negative: negativeAllowed(rules) });
  if (value === null) {
    // Why not: read it again without the limits, and name the one it broke.
    const loose = readDecimal(text, { decimals: 20, negative: true });
    if (loose === null) return 'Enter a number, such as 1,250.50.';
    if (!negativeAllowed(rules) && /^[-−]/.test(toLatinDigits(text).trim())) return `Enter ${rules.min ?? '0'} or more.`;
    if (places(rules) === 0) return 'Enter a whole number.';
    return `Use at most ${rules.decimals} decimal place${rules.decimals === 1 ? '' : 's'}.`;
  }
  if (rules.min !== undefined && compareDecimal(value, rules.min) < 0) return `Enter ${rules.min} or more.`;
  if (rules.max !== undefined && compareDecimal(value, rules.max) > 0) return `Enter ${rules.max} or less.`;
  return null;
}

export function NumberInput({
  value,
  onChange,
  onValue,
  kind = 'quantity',
  decimals = 4,
  min,
  max,
  unit,
  id,
  className,
  onBlur,
  ref,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'min' | 'max' | 'type' | 'width'> & {
  value: string;
  onChange: (text: string) => void;
  /** The canonical figure, or null when the text is empty or not a number — for a screen that wants the value. */
  onValue?: (value: string | null) => void;
  kind?: NumberKind;
  decimals?: number;
  min?: string;
  max?: string;
  unit?: string;
  ref?: Ref<HTMLInputElement>;
}) {
  const own = useId();
  const rules: Rules = { kind, decimals, min, max };
  // The problem last said aloud: set when the person leaves the box, cleared as soon as the text is right.
  const [problem, setProblem] = useState<string | null>(null);
  const unitId = unit ? `${id ?? own}-unit` : undefined;
  const errorId = problem ? `${id ?? own}-problem` : undefined;
  return (
    <span className="wb-number">
      <span className="wb-number__row">
        <Input
          {...props}
          ref={ref}
          id={id}
          kind={kind}
          className={className}
          value={value}
          aria-describedby={[describedBy, unitId, errorId].filter(Boolean).join(' ') || undefined}
          aria-invalid={invalid || problem ? true : undefined}
          onChange={(e) => {
            const text = e.target.value;
            onChange(text);
            onValue?.(numberValue(text, rules));
            if (problem && !problemWith(text, rules)) setProblem(null);
          }}
          onBlur={(e) => {
            setProblem(problemWith(value, rules));
            onBlur?.(e);
          }}
        />
        {unit ? (
          <span className="wb-number__unit" id={unitId}>
            {unit}
          </span>
        ) : null}
      </span>
      {problem ? (
        <span className="wb-error" id={errorId} role="alert">
          {problem}
        </span>
      ) : null}
    </span>
  );
}
NumberInput.wbLabellable = true;
